import React, { useState, useRef, useEffect } from 'react';
import { TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type EditableStatusCellProps = {
  isPremium: boolean;
  isAdmin: boolean;
  onUpdatePremium: (isPremium: boolean) => void;
  onUpdateAdmin: (isAdmin: boolean) => void;
  className?: string;
};

const EditableStatusCell = ({
  isPremium,
  isAdmin,
  onUpdatePremium,
  onUpdateAdmin,
  className = '',
}: EditableStatusCellProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editPremium, setEditPremium] = useState(isPremium);
  const [editAdmin, setEditAdmin] = useState(isAdmin);
  const selectRef = useRef<HTMLDivElement>(null);

  // Focus select when editing starts
  useEffect(() => {
    if (isEditing && selectRef.current) {
      const selectElement = selectRef.current.querySelector('button');
      if (selectElement) {
        selectElement.focus();
      }
    }
  }, [isEditing]);

  const handleDoubleClick = () => {
    setIsEditing(true);
    setEditPremium(isPremium);
    setEditAdmin(isAdmin);
  };

  const handleSave = () => {
    if (editPremium !== isPremium) {
      onUpdatePremium(editPremium);
    }

    if (editAdmin !== isAdmin) {
      onUpdateAdmin(editAdmin);
    }

    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditPremium(isPremium);
    setEditAdmin(isAdmin);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleStatusChange = (value: string) => {
    if (value === 'premium') {
      setEditPremium(true);
      setEditAdmin(false);
    } else if (value === 'admin') {
      setEditPremium(false);
      setEditAdmin(true);
    } else if (value === 'premium-admin') {
      setEditPremium(true);
      setEditAdmin(true);
    } else {
      // free
      setEditPremium(false);
      setEditAdmin(false);
    }
  };

  // Determine current status for select value
  const getCurrentStatus = () => {
    if (editPremium && editAdmin) return 'premium-admin';
    if (editPremium) return 'premium';
    if (editAdmin) return 'admin';
    return 'free';
  };

  return (
    <TableCell
      className={`${className} ${isEditing ? 'p-0' : ''}`}
      onDoubleClick={handleDoubleClick}
    >
      {isEditing ? (
        <div className="flex items-center">
          <div ref={selectRef} className="min-w-[140px]">
            <Select value={getCurrentStatus()} onValueChange={handleStatusChange}>
              <SelectTrigger onKeyDown={handleKeyDown}>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="premium-admin">Premium + Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleSave}
              title="Save"
            >
              <Check className="h-4 w-4 text-green-500" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleCancel}
              title="Cancel"
            >
              <X className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="cursor-pointer hover:text-primary" title="Double-click to edit">
          {isPremium ? (
            <Badge variant="default" className="bg-success text-success-foreground">
              Premium
            </Badge>
          ) : (
            <Badge variant="outline">Free</Badge>
          )}
          {isAdmin && (
            <Badge variant="default" className="bg-blue-500 text-white ml-2">
              Admin
            </Badge>
          )}
        </div>
      )}
    </TableCell>
  );
};

export default EditableStatusCell;
